import React, { useState } from 'react';
import {
  PencilIcon,
  XMarkIcon,
  UserIcon,
  IdentificationIcon,
  HeartIcon,
  PhoneIcon,
  ExclamationTriangleIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import type { LinkedUserContact, Player } from '../../types/player';
import { getMedicalStatus, formatMedicalDate } from '../../utils/playerMedical';
import AbsencePanel from './AbsencePanel';
import { absenceBadgeClass, describeAbsence } from '../../utils/absence';
import AiButton from '../ai/AiButton';
import AiPlayerEvaluationDialog from '../ai/AiPlayerEvaluationDialog';
import HelpHint from '../help/HelpHint';
import { useAiStatus } from '../../hooks/useAiStatus';
import { useEscapeToClose } from '../../hooks/useEscapeToClose';

interface PlayerDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  player: Player | null;
  /** Ha nincs megadva, a szerkesztés gomb nem jelenik meg (nincs jogosultság). */
  onEdit?: () => void;
  /** Inaktiválás / visszaaktiválás. Ha nincs megadva, a gomb nem jelenik meg. */
  onToggleActive?: () => void;
  /** Betegség/sérülés bejelentése után (a lista jelvényeinek frissítéséhez). */
  onAbsenceChanged?: () => void;
}

/** Telefonszám, ami mobilon egy koppintással hívható. */
const PhoneLink: React.FC<{ phone?: string | null }> = ({ phone }) =>
  phone ? (
    <a href={`tel:${phone.replace(/[^+\d]/g, '')}`} className="inline-flex items-center gap-1 text-primary-700 hover:underline">
      <PhoneIcon className="h-4 w-4" />
      {phone}
    </a>
  ) : null;

/** Egy kapcsolt személy (szülő, játékos fiók): név, email, telefon. */
const ContactPerson: React.FC<{ label: string; person?: LinkedUserContact | null }> = ({ label, person }) => (
  <div className="col-span-2 sm:col-span-1">
    <dt className="text-xs text-gray-400">{label}</dt>
    <dd className="text-sm text-gray-900 break-words">
      {person ? (
        <>
          <div>{person.name}</div>
          <a href={`mailto:${person.email}`} className="text-xs text-gray-500 hover:underline">
            {person.email}
          </a>
          {person.phone ? (
            <div>
              <PhoneLink phone={person.phone} />
            </div>
          ) : (
            <div className="text-xs text-gray-400">nincs telefonszám megadva</div>
          )}
        </>
      ) : (
        <span className="text-gray-400">-</span>
      )}
    </dd>
  </div>
);

/** Egy címke-érték pár az adatlapon. Üres érték helyén "-" jelenik meg. */
const Field: React.FC<{ label: string; value?: string | null; className?: string }> = ({
  label,
  value,
  className,
}) => (
  <div className={className}>
    <dt className="text-xs text-gray-400">{label}</dt>
    <dd className="text-sm text-gray-900 break-words">
      {value && value.trim() !== '' ? value : <span className="text-gray-400">-</span>}
    </dd>
  </div>
);

const Section: React.FC<{
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, icon, children }) => (
  <div>
    <div className="flex items-center gap-2 mb-2">
      <span className="text-gray-400">{icon}</span>
      <h4 className="text-sm font-semibold text-gray-700">{title}</h4>
    </div>
    <dl className="grid grid-cols-2 gap-x-4 gap-y-3">{children}</dl>
  </div>
);

/**
 * A játékos adatainak megtekintése szerkesztés nélkül. A kártyára/sorra
 * kattintva nyílik meg; innen egy gombbal lehet szerkesztésre váltani.
 */
const PlayerDetailModal: React.FC<PlayerDetailModalProps> = ({
  isOpen,
  onClose,
  player,
  onEdit,
  onToggleActive,
  onAbsenceChanged,
}) => {
  const ai = useAiStatus();
  const [evaluationOpen, setEvaluationOpen] = useState(false);

  useEscapeToClose(isOpen && !!player, onClose);

  if (!isOpen || !player) return null;

  const medicalStatus = getMedicalStatus(player);
  const ageGroups =
    player.ageGroups && player.ageGroups.length > 0 ? player.ageGroups.join(', ') : player.ageGroup;

  const formatDate = (value?: string) => {
    if (!value) return '';
    const date = new Date(value);
    return isNaN(date.getTime()) ? value : date.toLocaleDateString('hu-HU');
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true" aria-label={player.name}>
      {evaluationOpen && (
        <AiPlayerEvaluationDialog
          playerId={player.id}
          playerName={player.name}
          isOpen
          onClose={() => setEvaluationOpen(false)}
        />
      )}
      <div className="flex min-h-full items-stretch justify-center md:items-center md:p-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        {/* text-left: az App.css globális text-align: center szabályát írja felül */}
        <div className="modal-panel-mobile relative flex w-full flex-col bg-white text-left shadow-xl md:max-w-2xl md:rounded-lg">
          {/* Fejléc */}
          <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 p-4 md:p-6 md:pb-4">
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-gray-900 break-words">{player.name}</h3>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                {player.gender && (
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                      player.gender === 'Férfi' ? 'bg-blue-100 text-blue-800' : 'bg-pink-100 text-pink-800'
                    }`}
                  >
                    {player.gender}
                  </span>
                )}
                {ageGroups && (
                  <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                    {ageGroups}
                  </span>
                )}
                {player.isInactive && (
                  <span className="inline-flex items-center rounded-full bg-gray-700 px-2 py-0.5 text-xs font-medium text-white">
                    Inaktív
                  </span>
                )}
                {player.currentAbsence && (
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${absenceBadgeClass(player.currentAbsence.type)}`}
                  >
                    {describeAbsence(player.currentAbsence)}
                  </span>
                )}
              </div>
              {player.isInactive && (
                <p className="mt-2 text-sm text-gray-500">
                  Inaktív
                  {player.inactivatedAt && ` ${formatDate(player.inactivatedAt)} óta`}
                  {player.inactiveReason && ` · ${player.inactiveReason}`}
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={onClose}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-500 transition-colors"
              aria-label="Bezárás"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Adatok */}
          <div className="flex-1 space-y-6 overflow-y-auto p-4 md:p-6">
            {/* Amit az edzőnek a pályán azonnal tudnia kell */}
            {player.medicalNotes && (
              <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-600" />
                <div>
                  <p className="text-sm font-semibold text-amber-900">Egészségügyi tudnivalók</p>
                  <p className="whitespace-pre-line text-sm text-amber-900">{player.medicalNotes}</p>
                </div>
              </div>
            )}

            <Section title="Elérhetőségek" icon={<PhoneIcon className="h-5 w-5" />}>
              <ContactPerson label="Szülő" person={player.parentUser} />
              <ContactPerson label="Második szülő" person={player.secondParentUser} />
              <div>
                <dt className="text-xs text-gray-400">A játékos telefonja</dt>
                <dd className="text-sm text-gray-900">
                  {player.phone ? <PhoneLink phone={player.phone} /> : <span className="text-gray-400">-</span>}
                </dd>
              </div>
              <Field label="Lakcím" value={player.address} />
              <div className="col-span-2">
                <dt className="text-xs text-gray-400">Vészhelyzeti kontakt</dt>
                <dd className="text-sm text-gray-900">
                  {player.emergencyContactName || player.emergencyContactPhone ? (
                    <span className="flex flex-wrap items-center gap-x-3">
                      <span>
                        {player.emergencyContactName}
                        {player.emergencyContactRelation && (
                          <span className="text-gray-500"> ({player.emergencyContactRelation})</span>
                        )}
                      </span>
                      <PhoneLink phone={player.emergencyContactPhone} />
                    </span>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </dd>
              </div>
              {player.user && (
                <Field
                  label="Játékos fiók"
                  value={`${player.user.name} (${player.user.email})`}
                  className="col-span-2"
                />
              )}
            </Section>

            <Section title="Alapadatok" icon={<UserIcon className="h-5 w-5" />}>
              <Field label="Születési dátum" value={formatDate(player.birth)} />
              <Field label="Születési hely" value={player.placeOfBirth} />
              <Field label="Születési ország" value={player.countryOfBirth} />
              <Field label="Állampolgárság" value={player.citizen} />
              <Field label="Anyja neve" value={player.mothersName} className="col-span-2" />
            </Section>

            <Section title="Azonosítók" icon={<IdentificationIcon className="h-5 w-5" />}>
              <Field label="IFA kód" value={player.ifaCode} />
              <Field label="TAJ szám" value={player.taj} />
            </Section>

            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-gray-400">
                  <HeartIcon className="h-5 w-5" />
                </span>
                <h4 className="text-sm font-semibold text-gray-700">Sportorvosi</h4>
              </div>
              {medicalStatus === 'valid' ? (
                <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-1 text-xs font-medium text-green-800">
                  Érvényes: {formatMedicalDate(player.sportDoctorValidity)}
                </span>
              ) : medicalStatus === 'expired' ? (
                <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-1 text-xs font-medium text-red-800">
                  Lejárt: {formatMedicalDate(player.sportDoctorValidity)}
                </span>
              ) : (
                <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-1 text-xs font-medium text-red-800">
                  Nincs adat
                </span>
              )}
            </div>

            <div>
              <div className="mb-2 flex items-center gap-2">
                <span className="text-gray-400">
                  <CalendarDaysIcon className="h-5 w-5" />
                </span>
                <h4 className="text-sm font-semibold text-gray-700">Betegség, sérülés</h4>
              </div>
              <AbsencePanel playerId={player.id} playerName={player.name} onChanged={onAbsenceChanged} />
            </div>
          </div>

          {/* Műveletek */}
          <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-gray-200 p-4 pb-safe-4 md:flex-row md:justify-end md:gap-3 md:p-6">
            <button
              type="button"
              onClick={onClose}
              className="w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 md:w-auto"
            >
              Bezárás
            </button>

            {ai.available && (
              <span className="inline-flex w-full items-center justify-center gap-1 md:w-auto">
                <AiButton onClick={() => setEvaluationOpen(true)} className="flex-1 justify-center md:flex-none">
                  Értékelés AI-val
                </AiButton>
                <HelpHint id="players.aiEvaluation" />
              </span>
            )}

            {onToggleActive && (
              <button
                type="button"
                onClick={onToggleActive}
                className="w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 md:w-auto"
              >
                {player.isInactive ? 'Visszaaktiválás' : 'Inaktiválás'}
              </button>
            )}

            {onEdit && (
              <button
                type="button"
                onClick={onEdit}
                className="flex w-full items-center justify-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 md:w-auto"
              >
                <PencilIcon className="h-4 w-4" />
                Szerkesztés
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayerDetailModal;
